import { useRef } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"

import { SearchableText } from "../../../../search/Searchable"

import { useInlineClampedText } from "../../drawer/messages/useInlineClampedText"

const ELLIPSIS = "…"

type StyleProps = Readonly<{
    maxLines: number
}>

const useStyles = makeStyles<StyleProps>()((theme, { maxLines }) => ({
    container: {
        position: "relative",
        width: "100%",
    },
    title: {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: maxLines,
        overflow: "hidden",
        lineHeight: "1.5rem",
        overflowWrap: "anywhere",
        wordBreak: "normal",
    },
    iconInline: {
        display: "inline-flex",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        marginLeft: ".5rem",
    },
    infoButton: {
        padding: "0",
        position: "relative",
        bottom: ".125rem",
        color: theme.palette.tailwind.slate[400],
        transition: "all 0.3s ease-out",
        fontSize: "1rem",
        "&:hover": {
            color: theme.palette.tailwind.slate[500],
        },
    },
    measurerRoot: {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        top: "-10000px",
        left: "-10000px",
    },
    measurerText: {
        lineHeight: "1.5rem",
        overflowWrap: "anywhere",
        wordBreak: "normal",
    },
}))

type ProductCardTitleProps = Readonly<{
    name: string
    searchRegex?: RegExp
    maxLines?: number
    // When true, the info icon is only shown if the name is actually truncated.
    infoOnlyWhenTruncated?: boolean
    titleClassName?: string
    onInfoClick: () => void
}>

export function ProductCardTitle({
    name,
    searchRegex,
    maxLines = 2,
    infoOnlyWhenTruncated = false,
    titleClassName,
    onInfoClick,
}: ProductCardTitleProps) {
    const { classes, cx } = useStyles({ maxLines })

    const observeSizeRef = useRef<HTMLDivElement | null>(null)
    const measureRef = useRef<HTMLElement | null>(null)
    const measurePrefixRef = useRef<HTMLSpanElement | null>(null)
    const measureSuffixRef = useRef<HTMLSpanElement | null>(null)

    const { previewText, isTruncated } = useInlineClampedText({
        fullText: name,
        maxLines,
        suffixText: ELLIPSIS,
        suffixExtraEmWidth: 1.5,
        measureRef,
        measurePrefixRef,
        measureSuffixRef,
        observeSizeRef,
    })

    const titleContent = (
        <Box ref={observeSizeRef} className={classes.container}>
            <Typography variant="subtitle1" className={cx(classes.title, titleClassName)}>
                <SearchableText search={searchRegex}>
                    <span>{isTruncated ? previewText : name}</span>
                </SearchableText>
                {isTruncated && <span>{ELLIPSIS}</span>}
                {(!infoOnlyWhenTruncated || isTruncated) && (
                    <span className={classes.iconInline}>
                        <IconButton onClick={onInfoClick} className={classes.infoButton} size="small">
                            <FontAwesomeIcon icon={faCircleInfo} />
                        </IconButton>
                    </span>
                )}
            </Typography>
        </Box>
    )

    return (
        <>
            {isTruncated ? (
                <Tooltip title={name} arrow>
                    {titleContent}
                </Tooltip>
            ) : (
                titleContent
            )}

            <Box className={classes.measurerRoot} aria-hidden>
                <Typography variant="subtitle1" className={classes.measurerText} ref={measureRef}>
                    <span ref={measurePrefixRef} />
                    <span ref={measureSuffixRef} />
                </Typography>
            </Box>
        </>
    )
}
